import type { FetchResult } from '@apollo/client';
import { Observable } from '@apollo/client';
import type { ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';

import { DEFAULT_AUTH_ERROR_CODES } from './authErrors';
import { promiseToObservable } from './promiseToObservable';

function defaultErrorFilter(error?: string) {
  return !!error && DEFAULT_AUTH_ERROR_CODES.includes(error);
}

/**
 * Whether the app got the session back, and therefore whether sending the
 * operation again can do anything but fail the same way.
 */
export type AuthErrorRecovery = 'session-recovered' | 'session-not-recovered';

export type AuthErrorHandler = (code?: string) => Promise<AuthErrorRecovery>;

/**
 * Operation context that keeps this link from acting on the operation's auth
 * errors. Meant for the token refresh itself: routed back into recovery, a
 * refresh that fails with UNAUTHENTICATED would start another refresh through
 * this same link, without end and without ever letting its caller sign out.
 */
export const skipAuthErrorHandlingContext = {
  skipAuthErrorHandling: true,
} as const;

export const HandleAuthErrorResponseLink = (
  onAuthError: AuthErrorHandler,
  isAuthError?: (code?: string) => boolean
) => {
  const errorFilter =
    typeof isAuthError === 'function' ? isAuthError : defaultErrorFilter;

  return onError((errorResponse: ErrorResponse) => {
    const { graphQLErrors, networkError, operation, forward } = errorResponse;
    const skipAuthErrorHandling =
      operation.getContext().skipAuthErrorHandling === true;

    if (networkError) {
      console.error(
        `[🌏 Network error]: Operation: ${operation.operationName}, Message: ${networkError}`
      );
    }

    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        const { message, extensions } = err;
        const errorCode = extensions?.code;

        console.error(
          `[⚠️ GraphQL error]: Operation: ${operation.operationName}, Message: ${message}, Code: ${errorCode}`
        );

        if (skipAuthErrorHandling || !errorFilter(errorCode)) {
          continue;
        }

        return promiseToObservable(onAuthError(errorCode)).flatMap(
          (recovery) => {
            if (recovery === 'session-recovered') {
              return forward(operation);
            }

            return passThroughOriginalResult(errorResponse);
          }
        );
      }
    }
  });
};

/**
 * Hands the caller exactly what the server sent, as though this link had only
 * logged the error.
 *
 * The session was not recovered, so replaying the operation could only spend a
 * second request on the same failure — and because a replay is subscribed
 * downstream of this link, that second failure is the one Apollo turns into an
 * `ApolloError` for the caller (RWR-19755 / Sentry PAYROLL-WEB-D-J5). The
 * operation must still terminate: consumers such as the classic bridge's widget
 * host stay mounted with a no-op `onLogout`, so an operation left unsettled
 * would keep their loading state up for good.
 */
function passThroughOriginalResult({ response, networkError }: ErrorResponse) {
  return new Observable<FetchResult>((subscriber) => {
    if (response) {
      subscriber.next(response);
      subscriber.complete();
      return;
    }

    subscriber.error(networkError);
  });
}
